import { Directive, inject, input, OnChanges } from '@angular/core';
import { Router, RouterLink, UrlSegment, UrlSegmentGroup } from '@angular/router';
import { LanguageService } from '../../services/language/language.service';
import { isPathInRoutes } from '../../initializers/route-resolver.initializer';
import { VanillaRoutingService } from '../../services/vanilla-routing.service';
import { PageRegistryService } from '../../services/page-registry.service';

@Directive({
  selector: 'a[routerLinkWithLang]',
  host: {
    '(click)': 'onClick($event.button, $event.ctrlKey, $event.shiftKey, $event.altKey, $event.metaKey)',
  },
})
export class RouterLinkWithLang implements OnChanges {
  private readonly routerLinkDirective = inject(RouterLink);
  private readonly router = inject(Router);
  private readonly languageService = inject(LanguageService);
  private readonly vanillaRoutingService = inject(VanillaRoutingService);
  private readonly pageRegistryService = inject(PageRegistryService);

  /**
   * RouterLink's own input, read here only so that a link bound to something that changes - a list
   * card showing the variant last hovered - gets its language prefix back each time RouterLink
   * rebuilds it.
   */
  readonly routerLink = input<unknown>();

  ngOnChanges() {
    const routeLang = this.languageService.getCurrentRouteLang();
    if (!routeLang) {
      return;
    }

    const urlTree = this.routerLinkDirective.urlTree;
    if (!urlTree.root.children.primary) {
      urlTree.root.children.primary = new UrlSegmentGroup([], {});
    }
    urlTree.root.children.primary.segments.unshift(new UrlSegment(routeLang, {}));
    this.routerLinkDirective.fragment = undefined;
    this.routerLinkDirective.queryParams = null;
    this.routerLinkDirective.queryParamsHandling = null;
    this.routerLinkDirective.preserveFragment = null;
    this.routerLinkDirective.relativeTo = null;
    this.routerLinkDirective.routerLink = urlTree;
    this.routerLinkDirective['ngOnChanges']();
  }

  onClick(
    button: number,
    ctrlKey: boolean,
    shiftKey: boolean,
    altKey: boolean,
    metaKey: boolean,
  ): boolean {
    if (button !== 0 || ctrlKey || shiftKey || altKey || metaKey) {
      return true;
    }

    if (typeof this.routerLinkDirective.target === 'string' && this.routerLinkDirective.target !== '_self') {
      return true;
    }

    if (
      !isPathInRoutes(this.routerLinkDirective.href, this.router.config)
      && !this.pageRegistryService.isSlugInPath(this.routerLinkDirective.href)
    ) {
      this.vanillaRoutingService.isVanillaRoutingInProgress = true;
      window.location.assign(this.routerLinkDirective.href);
      return false;
    }
  }
}
